import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Badge, DateTimePicker, Counter, GroupedInputBox, LabeledField, SEO } from '../../../components/common';
import TransferLanding from '../../../components/service/TransferLanding';
import { useSearchFlow, SearchFlowModals } from '../../../components/service/SearchFlow';
import SearchButton from '../../../components/service/SearchButton';
import AddressSearch from '../../../components/service/AddressSearch';
import SkiResortSelector from '../../../components/service/SkiResortSelector';
import { pricingService } from '../../../api/services';
import useHolidayWarning from '../../../hooks/useHolidayWarning';
import HolidayWarningModal from '../../../components/service/HolidayWarningModal';
import { useFieldGuide } from '../../../hooks/useFieldGuide';
import { loadRecentSearch, saveRecentSearch } from '../../../hooks/useRecentSearch';
import { usePendingSearch } from '../../../hooks/usePendingSearch';
import { getDefaultBookingDateTime } from '../../../utils/dateUtils';


const FIELDS = [0, 1, 2, 3, 4];

export default function TransferSki() {
  const { t } = useTranslation();
  const flow = useSearchFlow('ski');
  const { isHighlighted, onFocusField, wrapChange } = useFieldGuide(FIELDS);
  const recent = loadRecentSearch('ski', 4);
  const [departure, setDeparture] = useState('');
  const [departureName, setDepartureName] = useState('');
  const [departureNameI18n, setDepartureNameI18n] = useState(''); // #216: 표시 전용 현지화 명칭
  const [skiResort, setSkiResort] = useState('');
  const [destination, setDestination] = useState('');
  const [destinationName, setDestinationName] = useState('');
  const [destinationNameI18n, setDestinationNameI18n] = useState(''); // #216: 표시 전용 현지화 명칭 (mobile/desktop 공유)
  const [sameAsDeparture, setSameAsDeparture] = useState(false);
  const [date, setDate] = useState(() => getDefaultBookingDateTime());
  const [people, setPeople] = useState(4);

  const { holidayInfo } = useHolidayWarning(date);
  const [holidayModal, setHolidayModal] = useState(null);
  const pendingSearchRef = useRef(null);
  // Restore pending search data after login redirect
  usePendingSearch('ski', useCallback((data) => {
    if (data.departure) setDeparture(data.departure);
    if (data.dateTime) setDate(data.dateTime);
    if (data.people) setPeople(data.people);
    if (data.venue) setSkiResort(data.venue);
  }, []));

  const [skiResortName, setSkiResortName] = useState(recent.skiResortName || '');
  const [skiResortNameKo, setSkiResortNameKo] = useState(recent.skiResortNameKo || ''); // #217: BE 매칭용 한글명
  const [skiResortAddress, setSkiResortAddress] = useState('');
  const handleSkiResortChange = (val, label, address, nameKo) => {
    setSkiResort(val);
    if (label) setSkiResortName(label);
    if (nameKo || label) setSkiResortNameKo(nameKo || label);
    setSkiResortAddress(address || label || '');
  };

  const handleSameAsDeparture = () => {
    const next = !sameAsDeparture;
    setSameAsDeparture(next);
    if (next) { // 체크: 도착지를 출발지로 미러 (표시 명칭까지 — #216 한글 회귀 방지)
      setDestination(departure); setDestinationName(departureName); setDestinationNameI18n(departureNameI18n || departureName);
    } else { // 해제: 미러였던 값 비움 → 사용자 재선택
      setDestination(''); setDestinationName(''); setDestinationNameI18n('');
    }
  };

  return (
    <>
    <SEO serviceType="Ski Resort Transfer" renderH1 />
    <TransferLanding
      heroImg="ski"

    >
      <Badge variant="time">{t('form.base_time_9h')}</Badge>

      {/* 3-field row: 출발지 → 스키장 → 도착지 */}
      <div className="lg:hidden flex flex-col gap-2">
        <GroupedInputBox>
          <AddressSearch value={departure} displayName={departureNameI18n || departureName} onChange={(address, name, nameI18n) => { setDeparture(address); setDepartureName(name || address); setDepartureNameI18n(nameI18n || name || address); }} placeholder={t('form.select_departure')} variant="borderless" restriction={{ message: t('form.region_restriction_seoul_only') }} />
          <SkiResortSelector value={skiResort} onChange={handleSkiResortChange} placeholder={t('form.select_ski_resort')} variant="borderless" />
          <AddressSearch value={sameAsDeparture ? (departureNameI18n || departureName || departure) : destination} displayName={sameAsDeparture ? (departureNameI18n || departureName) : destinationNameI18n} onChange={(address, name, nameI18n) => { setSameAsDeparture(false); setDestination(address); setDestinationName(name || address); setDestinationNameI18n(nameI18n || name || address); }} placeholder={t('form.select_destination')} variant="borderless" restriction={{ message: t('form.region_restriction_seoul_gyeonggi'), allowGyeonggi: true }} />
        </GroupedInputBox>
        {departure && (
          <button
            type="button"
            onClick={handleSameAsDeparture}
            className={`inline-flex items-center gap-1.5 text-2sm font-medium transition ${
              sameAsDeparture ? 'text-orange-500' : 'text-mono-500 hover:text-mono-700'
            }`}
          >
            <Icon name={sameAsDeparture ? 'check-filled' : 'check-circle'} size={16} />
            {t('form.same_as_departure')}
          </button>
        )}
      </div>
      <div className="hidden lg:flex lg:items-start gap-2.5">
        <LabeledField label={t('form.departure')} highlighted={isHighlighted(0)} onFocusCapture={() => onFocusField(0)} hint={t('form.region_restriction_seoul_only')}>
          <AddressSearch
            value={departure}
            displayName={departureNameI18n || departureName}
            onChange={(address, name, nameI18n) => { wrapChange(0, setDeparture)(address); setDepartureName(name || address); setDepartureNameI18n(nameI18n || name || address); }}
            placeholder={t('form.select_departure')}
            restriction={{ message: t('form.region_restriction_seoul_only') }}
          />
        </LabeledField>
        <div className="flex items-center justify-center w-6 h-12 mt-6">
          <Icon name="arrow-oneway" size={24} className="text-mono-300" />
        </div>
        <LabeledField label={t('form.ski_resort')} highlighted={isHighlighted(1)} onFocusCapture={() => onFocusField(1)} hint={t('form.region_restriction_seoul_gangwon')}>
          <SkiResortSelector value={skiResort} onChange={(...args) => wrapChange(1, handleSkiResortChange)(...args)} placeholder={t('form.select_ski_resort')} />
        </LabeledField>
        <div className="flex items-center justify-center w-6 h-12 mt-6">
          <Icon name="arrow-oneway" size={24} className="text-mono-300" />
        </div>
        <div className="flex-1 flex flex-col gap-1">
          <LabeledField label={t('form.destination')} highlighted={isHighlighted(2)} onFocusCapture={() => onFocusField(2)} hint={t('form.region_restriction_seoul_gyeonggi')}>
            <AddressSearch
              value={sameAsDeparture ? (departureNameI18n || departureName || departure) : destination}
              displayName={sameAsDeparture ? (departureNameI18n || departureName) : destinationNameI18n}
              onChange={(address, name, nameI18n) => { wrapChange(2, (a) => { setSameAsDeparture(false); setDestination(a); })(address); setDestinationName(name || address); setDestinationNameI18n(nameI18n || name || address); }}
              placeholder={t('form.select_destination')}
              restriction={{ message: t('form.region_restriction_seoul_gyeonggi'), allowGyeonggi: true }}
            />
          </LabeledField>
          {departure && (
            <button
              type="button"
              onClick={handleSameAsDeparture}
              className={`inline-flex items-center gap-1.5 text-2sm font-medium transition ${
                sameAsDeparture ? 'text-orange-500' : 'text-mono-500 hover:text-mono-700'
              }`}
            >
              <Icon name={sameAsDeparture ? 'check-filled' : 'check-circle'} size={16} />
              {t('form.same_as_departure')}
            </button>
          )}
        </div>
      </div>

      {/* Row: 차량 이용 일시 + 이용 인원 */}
      <div className="flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-11">
        <LabeledField label={t('form.vehicle_use_datetime')} highlighted={isHighlighted(3)} onFocusCapture={() => onFocusField(3)}>
          <DateTimePicker value={date} onChange={wrapChange(3, setDate)} maxMonths={2} blockHolidays />
        </LabeledField>
        <LabeledField label={t('form.use_passengers')} highlighted={isHighlighted(4)} onFocusCapture={() => onFocusField(4)}>
          <Counter value={people} onChange={wrapChange(4, setPeople)} />
        </LabeledField>
      </div>


<SearchButton onClick={() => {
        if (!departure) return alert(t('form.alert_input_departure'));
        if (!skiResort) return alert(t('form.alert_select_ski_resort'));
        if (!sameAsDeparture && !destination) return alert(t('form.alert_input_destination'));
        if (!date) return alert(t('form.alert_select_datetime'));
        // BugHerd #87 회귀 fix: blocked + surcharge 두 케이스 모두 모달 노출.
        const proceed = () => {
          const destDisplay = sameAsDeparture ? departure : destination;
          saveRecentSearch('ski', {
            departure, dateTime: date, people,
            skiResortId: skiResort, skiResortName, skiResortNameKo,
            destinationAddress: destDisplay,
          });
          flow.startSearch(date, {}, {
            departure, departureName,
            destination: destDisplay,
            destinationName: sameAsDeparture ? departureName : destinationName,
            destinationLabel: sameAsDeparture ? t('form.departure_same_as') : undefined,
            venue: skiResort, venueName: skiResortName, venueNameKo: skiResortNameKo, venueAddress: skiResortAddress, people,
          });
        };
        if (holidayInfo?.blocked) { setHolidayModal(holidayInfo); return; }
        if (holidayInfo?.amount > 0) { pendingSearchRef.current = proceed; setHolidayModal(holidayInfo); return; }
        proceed();
      }} />
    </TransferLanding>
    <SearchFlowModals flow={flow} dateTime={date} />
    {holidayModal && <HolidayWarningModal
      holiday={holidayModal}
      onClose={() => setHolidayModal(null)}
      onConfirm={() => { const fn = pendingSearchRef.current; pendingSearchRef.current = null; fn?.(); }}
    />}
    </>
  );
}
